<template>
  <div>
    <div
      v-if="warning"
      class="warning-block d-flex align-start mb-4 py-1 px-4"
      role="status"
      data-testid="billable-usage-export-warning"
    >
      <v-icon class="mr-2" icon="$warning" />
      <span class="body2">{{ warning }}</span>
    </div>
    <cui-billable-usage-export-form
      v-model:period="period"
      v-model:export-type="type"
      :periods="config.item.periods"
      :labels="labels"
    />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";
import {
  BillableUsageExportType,
  BillableUsageStatus,
  CuiBillableUsageExportForm,
  type BillableUsageExportValue,
  type CuiBillableUsageExportFormLabels,
} from "@coronet/cui";
import type { DialogDataConfig } from "@/_store/dialogs.module";
import {
  useMspBillableUsageStore,
  type BillableUsageExportDialogItem,
} from "@/_store/msp/msp-billable-usage.module.ts";

export default defineComponent({
  components: { CuiBillableUsageExportForm },
  props: {
    config: {
      type: Object as PropType<DialogDataConfig<BillableUsageExportDialogItem>>,
      required: true,
    },
  },
  emits: ["update:localValue", "update:valid"],
  setup(props, { emit }) {
    const { t } = useI18n();
    const billableUsageStore = useMspBillableUsageStore();
    const { checkedPeriodStatus: status } = storeToRefs(billableUsageStore);
    const { workspaceId } = props.config.item;
    const period = ref(props.config.item.period);
    const type = ref<BillableUsageExportType>(BillableUsageExportType.AGGREGATED);

    const labels = computed((): CuiBillableUsageExportFormLabels => ({
      monthLabel: t("modals.exportBillingUsage.monthLabel"),
      typeTitle: t("modals.exportBillingUsage.typeTitle"),
      aggregatedTitle: t("modals.exportBillingUsage.choices.aggregated.title"),
      aggregatedDescription: t("modals.exportBillingUsage.choices.aggregated.description"),
      fullTitle: t("modals.exportBillingUsage.choices.full.title"),
      fullDescription: t("modals.exportBillingUsage.choices.full.description"),
    }));

    // Why the picked month cannot be exported, if it cannot. A month whose status could not be checked
    // stays exportable: the backend answers 400 for a month without data, and the interceptor shows it.
    const warning = computed(() => {
      const checked = status.value.data;
      if (!period.value || status.value.loading || !checked) return "";
      if (checked.status === BillableUsageStatus.NOT_CALCULATED) {
        return t("modals.exportBillingUsage.notCalculatedWarning");
      }
      // A calculated month can have no billable usage; `hasUsage` is left out by an older backend.
      return checked.hasUsage === false ? t("modals.exportBillingUsage.noUsageWarning") : "";
    });
    const valid = computed(() => Boolean(period.value) && !status.value.loading && !warning.value);

    watch(
      period,
      (newPeriod) => {
        if (newPeriod) billableUsageStore.checkPeriodStatus(workspaceId, newPeriod);
      },
      { immediate: true }
    );

    watch(valid, (isValid) => emit("update:valid", isValid), { immediate: true });

    watch(
      [period, type],
      ([newPeriod, newType]) => {
        const value: BillableUsageExportValue = { type: newType, period: newPeriod };
        emit("update:localValue", value);
      },
      { immediate: true }
    );

    return { period, type, labels, warning };
  },
});
</script>
